import type { Messages } from "@/messages/buildCatalog";
import {
  Box,
  Button,
  OutlinedInput,
  Skeleton,
  Stack,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import type { PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { NumericFormat } from "react-number-format";

export interface Input {
  pretitle: string;
  title: string;
  explanation: string;
  input: React.ReactNode;
}

type AbfindungStrings = Messages["calculator"]["abfindung"];

export const getInputs = ({
  theme,
  abfindung,
  isMoreThan10,
  handleIsMoreThan10,
  monthlyIncome,
  handleChangeMonthlyIncome,
  handleConfirmMonthlyIncome,
  whenStartedWorking,
  handleWhenStartedWorking,
  handleIsCalculationStarted: handleConfirmWhenStartedWorking,
  beschäftigungszeit,
}: {
  theme: Theme;
  abfindung: AbfindungStrings;
  isMoreThan10: boolean | undefined;
  handleIsMoreThan10: (value: boolean | undefined) => void;
  monthlyIncome: number;
  handleChangeMonthlyIncome: (value: number) => void;
  isMonthlyIncomeConfirmed: boolean | undefined;
  handleConfirmMonthlyIncome: (value: boolean | undefined) => void;
  whenStartedWorking: Date | undefined;
  handleWhenStartedWorking: (value: Date | undefined) => void;
  handleIsCalculationStarted: (value: Date | undefined) => void;
  beschäftigungszeit: number;
}): Input[] => {
  const [step0, step1, step2] = abfindung.steps;

  return [
    {
      pretitle: step0.pretitle,
      title: step0.title,
      explanation: step0.explanation,
      input: (
        <>
          <ToggleButtonGroup
            size="small"
            color="primary"
            value={isMoreThan10}
            exclusive={true}
            onChange={(event, value: boolean) => {
              handleIsMoreThan10(value);
            }}
            aria-label={step0.ariaToggleGroup}
          >
            <ToggleButton value={true} aria-label={step0.ariaYes}>
              {step0.yes}
            </ToggleButton>

            <ToggleButton value={false} aria-label={step0.ariaNo}>
              {step0.no}
            </ToggleButton>
          </ToggleButtonGroup>
        </>
      ),
    },
    {
      pretitle: step1.pretitle,
      title: step1.title,
      explanation: step1.explanation,
      input: (
        <Stack gap={1}>
          <Box>
            <NumericFormat
              sx={{ backgroundColor: theme.palette.background.default }}
              size="medium"
              customInput={OutlinedInput}
              suffix=" €"
              value={monthlyIncome}
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={0}
              allowNegative={false}
              inputMode="numeric"
              aria-label={step1.ariaMonthlyGross}
              onValueChange={(values) => {
                const { floatValue } = values;
                handleChangeMonthlyIncome(floatValue || 0);
              }}
            />
          </Box>

          <Box>
            <Button
              variant="contained"
              onClick={() => handleConfirmMonthlyIncome(true)}
              size="small"
            >
              {step1.continue}
            </Button>
          </Box>
        </Stack>
      ),
    },
    {
      pretitle: step2.pretitle,
      title: step2.title,
      explanation: step2.explanation,
      input: (
        <>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              sx={{
                "& .MuiPickersCalendar-root": {
                  ...theme.typography.body2,
                },
              }}
              value={whenStartedWorking ? dayjs(whenStartedWorking) : null}
              onChange={(newValue) =>
                handleWhenStartedWorking(
                  newValue ? newValue.toDate() : undefined,
                )
              }
              views={["month", "year"]}
              openTo="month"
              slotProps={{
                day: {
                  "aria-label": step2.ariaPickDay,
                } satisfies Partial<PickersDayProps>,
              }}
            />
          </LocalizationProvider>

          <Typography variant="body2" fontWeight={600}>
            {abfindung.employmentRoundedPrefix}
            {whenStartedWorking ? (
              <NumericFormat
                style={{ fontWeight: 600 }}
                value={beschäftigungszeit}
                displayType="text"
                thousandSeparator={"."}
                decimalScale={0}
                suffix={
                  beschäftigungszeit !== 1
                    ? abfindung.summary.yearsSuffix
                    : abfindung.summary.yearSuffix
                }
                decimalSeparator={","}
              />
            ) : (
              <Skeleton
                component="span"
                variant="text"
                width={100}
                height={"1.5rem"}
              />
            )}
          </Typography>

          <Box>
            <Button
              variant="contained"
              onClick={() =>
                handleConfirmWhenStartedWorking(whenStartedWorking)
              }
              size="small"
            >
              {step2.startCalculation}
            </Button>
          </Box>
        </>
      ),
    },
  ];
};
